import React, { useEffect, useRef } from 'react';
import { cn } from '@newcore/ui/cn';
import { FontWeights } from '@newcore/ui/enums/font-weights';
import { TextSizes } from '@newcore/ui/enums/typography-text-sizes';
import { textVariants } from '@newcore/ui/variants/typography-text-variants';

export interface OtpInputProps {
  value: string[];
  onChange: (value: string[]) => void;
  onComplete?: () => void;
  hasError?: boolean;
  disabled?: boolean;
  autoFocus?: boolean;
  length?: number;
}

/**
 * Generic OTP Input Component
 * Renders individual input boxes for OTP codes with auto-focus, paste support, and keyboard navigation.
 * Numeric only (0-9) - shows numeric keypad on mobile.
 * For even-length codes, displays a visual gap in the middle (e.g., 6 characters: [3] [3], 8 characters: [4] [4])
 */
export const OtpInput: React.FC<OtpInputProps> = ({
  value,
  onChange,
  onComplete,
  hasError = false,
  disabled = false,
  autoFocus = true,
  length = 6,
}) => {
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const hasCalledOnCompleteRef = useRef(false);

  // Re-arm auto-submit whenever the code is no longer complete
  useEffect(() => {
    const isComplete =
      value.length === length && value.every(char => char.length === 1);

    if (!isComplete && hasCalledOnCompleteRef.current) {
      hasCalledOnCompleteRef.current = false;
    }
  }, [value, length]);

  // Check if all characters are filled and call onComplete ONCE (first time only)
  useEffect(() => {
    const isComplete =
      value.length === length && value.every(char => char.length === 1);

    if (isComplete && onComplete && !hasCalledOnCompleteRef.current) {
      hasCalledOnCompleteRef.current = true;
      onComplete();
    }
  }, [value, onComplete, length]);

  const applyPastedCode = (pastedText: string) => {
    const characters = pastedText.replace(/[^0-9]/g, '').slice(0, length);

    if (characters.length === 0) {
      return;
    }

    const newValue = [...value];

    for (let i = 0; i < characters.length && i < length; i++) {
      newValue[i] = characters[i];
    }

    onChange(newValue);

    const nextEmptyIndex = newValue.findIndex(v => !v);
    const focusIndex = nextEmptyIndex === -1 ? length - 1 : nextEmptyIndex;
    inputRefs.current[focusIndex]?.focus();
  };

  const handleChange = (index: number, inputValue: string) => {
    if (inputValue.length > 1) {
      // Paste via onChange (some browsers fire this instead of/in addition to onPaste)
      applyPastedCode(inputValue);

      return;
    }

    const digit = /^[0-9]$/.test(inputValue) ? inputValue : '';

    if (!digit) {
      return;
    } // Ignore non-numeric input

    const newValue = [...value];
    newValue[index] = digit;
    onChange(newValue);

    if (index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    const isPasteShortcut =
      (e.key === 'v' || e.key === 'V') && (e.metaKey || e.ctrlKey);
    const allowedKeys = ['Backspace', 'Tab', 'ArrowLeft', 'ArrowRight'];

    if (
      !/^[0-9]$/.test(e.key) &&
      !allowedKeys.includes(e.key) &&
      !isPasteShortcut
    ) {
      e.preventDefault();

      return;
    }

    if (e.key === 'Backspace') {
      if (!value[index] && index > 0) {
        // If current input is empty, clear previous and move focus there
        const newValue = [...value];
        newValue[index - 1] = '';
        onChange(newValue);
        inputRefs.current[index - 1]?.focus();
      } else {
        // Clear current input
        const newValue = [...value];
        newValue[index] = '';
        onChange(newValue);
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      e.preventDefault();
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < length - 1) {
      e.preventDefault();
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleBeforeInput = (e: React.FormEvent<HTMLInputElement>) => {
    const inputEvent = e.nativeEvent as InputEvent;

    if (inputEvent.inputType === 'insertFromPaste' && inputEvent.data) {
      e.preventDefault();
      applyPastedCode(inputEvent.data);
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const text = e.clipboardData.getData('text');

    if (text) {
      e.preventDefault();
      applyPastedCode(text);
    }
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    // Select the content when focusing
    e.target.select();
  };

  const inputClasses = cn(
    textVariants({ size: TextSizes.LG, weight: FontWeights.SEMIBOLD }),
    'w-10 h-12.5 text-center rounded-md border transition-colors',
    'bg-primary',
    'border-primary',
    'hover:border-utility-gray-400',
    'focus:border-fg-primary focus:outline-none',
    hasError && 'border-error hover:border-error focus:border-error',
    disabled && 'opacity-50 cursor-not-allowed'
  );

  // Calculate middle point for even-length codes to add visual gap
  const isEven = length % 2 === 0;
  const halfLength = Math.floor(length / 2);

  return (
    <div className='flex gap-2 justify-center'>
      {value.slice(0, length).map((digit, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <React.Fragment key={index}>
          {/* Add larger gap at the middle for even-length codes */}
          {isEven && index === halfLength && (
            <div className='w-4' aria-hidden='true' />
          )}
          <input
            ref={el => {
              inputRefs.current[index] = el;
            }}
            type='text'
            inputMode='numeric'
            pattern='[0-9]*'
            maxLength={1}
            value={digit}
            onBeforeInput={handleBeforeInput}
            onChange={e => handleChange(index, e.target.value)}
            onKeyDown={e => handleKeyDown(index, e)}
            onPaste={handlePaste}
            onFocus={handleFocus}
            disabled={disabled}
            autoFocus={autoFocus && index === 0}
            className={inputClasses}
            aria-label={`Character ${index + 1} of ${length}`}
            data-testid={`otp-input-${index}`}
          />
        </React.Fragment>
      ))}
    </div>
  );
};
